import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type AttendanceColumnKey =
  | 'employee'     // Always visible (sticky)
  | 'status'

  | 'checkIn'
  | 'checkOut'
  | 'workingTime'
  | 'overtime'
  | 'late'
  | 'halfDay'
  | 'earlyPunchIn'
  | 'autoCheckout'
  | 'punchInLocation'
  | 'punchOutLocation';

interface AttendanceTableState {
  columnVisibility: Record<AttendanceColumnKey, boolean>;
  toggleColumn: (column: AttendanceColumnKey) => void;
  setColumnVisibility: (column: AttendanceColumnKey, visible: boolean) => void;
  resetColumns: () => void;
}

const defaultVisibility: Record<AttendanceColumnKey, boolean> = {
  employee: true,        // Always visible
  status: true,

  checkIn: true,
  checkOut: true,
  workingTime: true,
  overtime: true,
  late: true,
  halfDay: false,
  earlyPunchIn: false,
  autoCheckout: false,
  punchInLocation: false,
  punchOutLocation: false,
};

export const useAttendanceTableStore = create<AttendanceTableState>()(
  persist(
    (set) => ({
      columnVisibility: defaultVisibility,

      toggleColumn: (column) =>
        set((state) => {
          if (column === 'employee') return state;
          return {
            columnVisibility: {
              ...state.columnVisibility,
              [column]: !state.columnVisibility[column],
            },
          };
        }),

      setColumnVisibility: (column, visible) =>
        set((state) => {
          if (column === 'employee' && !visible) return state;
          return {
            columnVisibility: {
              ...state.columnVisibility,
              [column]: visible,
            },
          };
        }),

      resetColumns: () =>
        set({
          columnVisibility: defaultVisibility,
        }),
    }),
    {
      name: 'attendance-table-storage',
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState: unknown, currentState: AttendanceTableState) => {
        const persisted = persistedState as Partial<AttendanceTableState> | undefined;
        return {
          ...currentState,
          columnVisibility: {
            ...defaultVisibility,
            ...(persisted?.columnVisibility || {}),
          },
        };
      },
    }
  )
);
